import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";
import { SITE } from "../lib/seoConfig";
import { ArrowRight, Check, ChevronRight, Sparkles } from "lucide-react";

/**
 * Public, crawlable SEO landings for routes that ALSO function as authenticated
 * app pages. When the user is signed in, App.js renders the real feature; when
 * the visitor is anonymous (or Googlebot), this component renders.
 *
 * Each variant has its own title/H1/content/FAQ + BreadcrumbList & FAQPage JSON-LD.
 */
/**
 * Static accent class map — Tailwind JIT needs literal class strings to detect them.
 * Don't switch this to template-string interpolation.
 */
const ACCENT = {
  emerald: { eyebrow: "text-emerald-700", bullet: "text-emerald-600" },
  indigo: { eyebrow: "text-indigo-700", bullet: "text-indigo-600" },
  rose: { eyebrow: "text-rose-700", bullet: "text-rose-600" },
  amber: { eyebrow: "text-amber-700", bullet: "text-amber-600" },
};

const VARIANTS = {
  "/app/grammar": {
    accent: "emerald",
    title: "Grammar Lab for Students | Interactive Grammar Practice & Drills — WriterSpark",
    description:
      "Interactive grammar drills with instant AI explanations — subject-verb agreement, commas, fragments, parallel structure, and more. Free practice for middle and high school students.",
    keywords: "grammar practice, grammar drills, grammar lab, student grammar, grammar mechanics, grammar exercises online",
    eyebrow: "Grammar Lab",
    h1: "Grammar Lab — Drills that Actually Stick",
    lead: "Targeted grammar drills with instant explanations. Practice the moves that get marked up most: subject-verb agreement, comma usage, fragments, parallel structure, modifier placement.",
    sections: [
      { h2: "Diagnostic drills",
        body: "Start with a quick diagnostic. WriterSpark identifies the grammar moves you miss most and routes you to focused 5-minute drills.",
        bullets: ["Subject-verb agreement", "Comma rules (lists, clauses, intros)", "Fragments and run-ons", "Parallel structure", "Pronoun-antecedent agreement"] },
      { h2: "Instant rule explanations",
        body: "Every wrong answer comes with a clear rule explanation written for your grade level — not a textbook quote.",
        bullets: ["Plain-English rule cards", "Worked examples in context", "Pattern memory checks"] },
      { h2: "Practice that compounds",
        body: "Daily streaks, growth badges, and rubric trend data keep students returning. The Grammar Lab feeds the larger essay coach with cleaner sentence-level writing.",
        bullets: ["Streaks + badges", "Bronze → Diamond growth paths", "Auto-integrates with Essay Coach"] },
    ],
    faqs: [
      { q: "Is the Grammar Lab free?",
        a: "Yes — every WriterSpark account gets free daily grammar drills. Premium unlocks unlimited daily sessions and additional drill types." },
      { q: "What grade levels does this work for?",
        a: "Elementary, middle, and high school. Drill difficulty and rule explanations adapt to the selected grade." },
      { q: "Will this help with SAT/ACT grammar?",
        a: "Yes — the Grammar Lab covers the conventions tested on the SAT Writing & Language and ACT English sections, including modifier placement, parallel structure, and idiom usage." },
    ],
  },

  "/app/sat-ap": {
    accent: "indigo",
    title: "SAT & AP Writing Prep | Timed Essay Coach with Rubric Scoring — WriterSpark",
    description:
      "Prepare for SAT and AP writing with timed essays scored on the College Board rubric. Instant feedback, evidence integration coaching, and revision strategies.",
    keywords: "SAT writing prep, AP writing prep, SAT essay practice, AP English essay, timed essay coach, College Board essay rubric",
    eyebrow: "SAT & AP Prep",
    h1: "SAT & AP Writing Prep with Rubric Scoring",
    lead: "Timed essay practice scored on the actual College Board rubric. WriterSpark coaches the argument moves AP graders care about — evidence selection, sophistication, and explanation depth.",
    sections: [
      { h2: "Timed essay sprints",
        body: "Practice under real conditions. 25-, 40-, and 50-minute essay timers match SAT and AP Language and AP Lit formats.",
        bullets: ["SAT-style argument prompts", "AP Lang rhetorical analysis", "AP Lit literary analysis", "Authentic timing pressure"] },
      { h2: "College Board rubric scoring",
        body: "Every essay scored on the College Board's row scoring (Thesis / Evidence & Commentary / Sophistication). See exactly which row dropped your composite.",
        bullets: ["Row 1: Thesis", "Row 2: Evidence + Commentary", "Row 3: Sophistication"] },
      { h2: "Targeted revision after each draft",
        body: "After each timed essay, WriterSpark gives ONE revision goal per pass — the highest-impact move you can make to climb the rubric.",
        bullets: ["One revision target per pass", "Mini-lessons on the move", "Re-score after revision"] },
    ],
    faqs: [
      { q: "Does this replace an SAT prep course?",
        a: "WriterSpark doesn't replace a full course — it makes the writing portion vastly more productive. Most students plateau on writing scores because they don't get enough timed essay reps with real feedback. WriterSpark fills exactly that gap." },
      { q: "Are the prompts current?",
        a: "Yes — prompts are modeled on recent College Board released essays and refreshed regularly. We don't reuse copyrighted prompts; ours are written in the same format." },
    ],
  },

  "/app/essay": {
    accent: "rose",
    title: "Essay Coach for Students | Rubric-Aligned AI Writing Feedback — WriterSpark",
    description:
      "Submit any essay and get rubric-aligned feedback at the paragraph level — thesis, evidence, analysis, organization, conventions. WriterSpark coaches every revision pass.",
    keywords: "essay coach, essay feedback, essay writing app, AI essay coach, rubric essay feedback, student essay help",
    eyebrow: "Essay Coach",
    h1: "Essay Coach — From Thesis to Final Draft",
    lead: "Submit any essay — argumentative, narrative, expository, analytical. WriterSpark scores on a standards-aligned rubric and gives paragraph-level feedback that coaches the next revision.",
    sections: [
      { h2: "Rubric scoring you can trust",
        body: "Scores on ideas, organization, voice, and conventions — with student-facing explanations for every score. No black-box grading.",
        bullets: ["4 rubric dimensions", "Score explanations from the student's own text", "Targeted revision goals"] },
      { h2: "Paragraph-by-paragraph feedback",
        body: "Per-paragraph callouts: weak thesis, missing analysis, transition gap, evidence cherry-picking. Real coaching at the level where revision happens.",
        bullets: ["Thesis specificity check", "Evidence integration analysis", "Transition variety", "Cohesion + coherence"] },
      { h2: "Revision Intelligence",
        body: "Every draft is saved. Watch rubric scores climb across drafts — proof that the revision actually worked.",
        bullets: ["Draft-over-draft trend lines", "Diff between drafts", "Revision Cycles dashboard"] },
    ],
    faqs: [
      { q: "Will WriterSpark write the essay for me?",
        a: "No. WriterSpark coaches — it never writes the draft. The platform asks questions, scores against rubrics, and gives revision suggestions, but the writing stays the student's own." },
      { q: "What rubric do you use?",
        a: "Default is a 4-point standards-aligned rubric across ideas, organization, voice, and conventions. AP/SAT modes switch to the College Board row scoring." },
    ],
  },

  "/app/practice": {
    accent: "amber",
    title: "Daily Writing Practice for Students | Prompts, Sprints & Growth Paths — WriterSpark",
    description:
      "Daily writing prompts and timed sprints for middle and high school students. Build fluency, hit streaks, and watch your writing skill grow over weeks.",
    keywords: "daily writing practice, writing prompts, timed writing sprints, student writing practice, daily essay practice",
    eyebrow: "Writing Practice",
    h1: "Daily Writing Practice — Build Real Fluency",
    lead: "Short, grade-adapted prompts that build writing fluency over time. Narrative, opinion, expository, and timed sprints — designed for 20-30 focused minutes a day.",
    sections: [
      { h2: "Daily prompts",
        body: "Fresh prompts every day, grade-banded so the topic actually clicks. Get a streak going and watch the fluency follow.",
        bullets: ["Narrative + opinion + expository", "Grade-adapted topics", "Streaks that reward consistency"] },
      { h2: "Timed sprints",
        body: "5-, 10-, and 25-minute sprints train fast thinking under pressure. Crucial for SAT/AP writing readiness — and for getting unstuck.",
        bullets: ["Speed-writing under timer", "Generation-mode (idea fluency)", "Polishing-mode (clean drafts)"] },
      { h2: "Growth Paths",
        body: "Bronze → Silver → Gold → Diamond paths for every writing skill. Track exactly where you've grown and what's next.",
        bullets: ["Per-skill leveling", "Badges + streaks", "Visible growth charts"] },
    ],
    faqs: [
      { q: "How long does a daily practice session take?",
        a: "Most students spend 20-30 minutes a day. A daily prompt + grammar drill + revision pass fits in that window." },
      { q: "Is daily writing practice really effective?",
        a: "Yes. Research consistently shows daily low-stakes writing practice builds fluency, vocabulary, and confidence faster than weekly graded assignments alone." },
    ],
  },
};

function Breadcrumbs({ label }) {
  return (
    <nav aria-label="Breadcrumb" className="text-sm text-slate-500 mb-6">
      <Link to="/" className="hover:text-slate-700">Home</Link>
      <ChevronRight size={14} className="inline mx-1 -mt-0.5" />
      <span className="text-slate-800 font-semibold">{label}</span>
    </nav>
  );
}

export default function FeaturePublic({ path }) {
  const v = VARIANTS[path];
  if (!v) return null;
  const accent = ACCENT[v.accent] || ACCENT.indigo;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.origin}/` },
        { "@type": "ListItem", position: 2, name: v.eyebrow, item: `${SITE.origin}${path}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: v.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="bg-white min-h-screen">
      <Seo
        path={path}
        title={v.title}
        description={v.description}
        keywords={v.keywords}
        schema={jsonLd}
      />
      <PublicHeader />
      <main className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
        <Breadcrumbs label={v.eyebrow} />

        <header className="mb-12">
          <span className={`text-xs uppercase font-bold tracking-widest ${accent.eyebrow}`}>{v.eyebrow}</span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>{v.h1}</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-3xl">{v.lead}</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to="/register" data-testid="feature-cta-register"
              className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-6 py-3 tactile-shadow">
              Start free <ArrowRight size={16} />
            </Link>
            <Link to="/login" data-testid="feature-cta-login"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-200 hover:border-indigo-300 text-slate-900 font-bold rounded-full px-6 py-3">
              I have an account
            </Link>
          </div>
        </header>

        <section className="space-y-10 mb-16">
          {v.sections.map((s, i) => (
            <article key={i}>
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>{s.h2}</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">{s.body}</p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check size={16} className={`${accent.bullet} mt-0.5 flex-shrink-0`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </section>

        <section className="mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: "Outfit" }}>Common questions</h2>
          <div className="space-y-4">
            {v.faqs.map((f, i) => (
              <details key={i} className="tactile-card p-5 group" data-testid={`feature-faq-${i}`}>
                <summary className="cursor-pointer font-bold text-slate-900 flex items-center justify-between gap-3">
                  <span>{f.q}</span>
                  <ChevronRight size={16} className="transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-xl font-bold mb-4" style={{ fontFamily: "Outfit" }}>Explore more WriterSpark tools</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(VARIANTS).filter(([k]) => k !== path).map(([k, vv]) => (
              <Link key={k} to={k} className="tactile-card p-4 hover:border-indigo-300 transition flex items-start gap-3">
                <Sparkles size={18} className="text-indigo-500 flex-shrink-0 mt-1" />
                <div>
                  <div className="font-bold text-sm">{vv.h1}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{vv.eyebrow}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="tactile-card p-8 sm:p-10 text-center mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: "Outfit" }}>Ready to start practicing?</h2>
          <p className="text-slate-600 mb-6 max-w-xl mx-auto">
            Free to start, no credit card required. Sign up and you&apos;ll land right back here — ready to write.
          </p>
          <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-7 py-3.5 tactile-shadow">
            Start free <ArrowRight size={16} />
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
